// Taşeron portalı — kabuk hesap menüsü (S37-1, Carbon 2a). Koyu 48px şeritte 48x48 baş-harf
// avatar tetikleyicisi; menü: kimlik + (isteğe bağlı) Profil + Dil radyo grubu + Çıkış.
// `AppHeader` ve `AppDownloadOnly` (Personeli şeridi) AYNI menüyü kullanır (design review S9).
//
// Dil değişimi `changeCulture` çözülene dek menü AÇIK kalır (`languageBusy` ile radyolar
// kilitli) — web-expert `AppHeader`'ın QA'dan geçmiş deseninin kopyası (app sınırı: import
// değil kopya). Çıkış: `logout()` + `queryClient.clear()` (S17 güvenlik F4 — önbellekte TC
// kimlik no + kişi adları olabilir) + `/login`.

import { changeCulture, getAvailableLanguages, logout, useAuthStore } from '@isgportal/shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, CircleUser, LogOut, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryClient } from '../lib/queryClient';
import { useSupplierMe } from '../lib/useSupplierMe';

/** Baş harf rozeti — ad + soyadın ilk harfleri; ikisi de yoksa `null` (defensif ikon düşer). */
function initialsOf(firstName?: string | null, lastName?: string | null): string | null {
  const a = firstName?.trim()?.[0];
  const b = lastName?.trim()?.[0];
  if (!a && !b) return null;
  return `${a ?? ''}${b ?? ''}`.toUpperCase();
}

export function UserMenu({ showProfile = true }: { showProfile?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const principal = useAuthStore((s) => s.principal);
  const { me } = useSupplierMe();
  const languages = getAvailableLanguages();

  const [menuOpen, setMenuOpen] = useState(false);
  const [languageBusy, setLanguageBusy] = useState(false);

  // Görünen kimlik: ad-soyad (self-profil) — yoksa e-posta/telefon (token) — yoksa "Hesap".
  const fullName = me ? `${me.firstName} ${me.lastName}`.trim() : '';
  const displayName = fullName || principal?.email || t('layout.account');
  const initials = initialsOf(me?.firstName, me?.lastName);

  async function onLanguageChange(culture: string) {
    if (culture === i18n.language) {
      setMenuOpen(false);
      return;
    }
    setLanguageBusy(true);
    try {
      await changeCulture(culture);
    } finally {
      setLanguageBusy(false);
      setMenuOpen(false);
    }
  }

  function onLogout() {
    logout();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenu.Trigger
        className="flex h-12 w-12 items-center justify-center text-shell-fg-muted hover:bg-shell-hover hover:text-shell-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-shell-fg"
        aria-label={t('layout.user.menu')}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-shell-avatar text-[11px] font-semibold text-fg">
          {initials ?? <UserRound className="h-3.5 w-3.5" aria-hidden="true" />}
        </span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-[var(--z-dropdown)] min-w-[16rem] max-w-[20rem] overflow-hidden rounded-md border border-border bg-surface p-1 shadow-md"
        >
          <div className="px-2 py-1.5">
            <p className="text-xs text-muted">{t('layout.user.signedInAs')}</p>
            <p className="truncate text-sm font-medium text-fg" title={displayName}>
              {displayName}
            </p>
            {me?.emailOrPhone ? (
              <p className="truncate text-xs text-muted" title={me.emailOrPhone}>
                {me.emailOrPhone}
              </p>
            ) : null}
            {me?.companyName?.trim() ? (
              <p className="truncate text-xs text-muted" title={me.companyName.trim()}>
                <span className="sr-only">{t('layout.user.company')}: </span>
                {me.companyName.trim()}
              </p>
            ) : null}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {showProfile ? (
            <>
              <DropdownMenu.Item
                onSelect={() => navigate('/profile')}
                className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted"
              >
                <CircleUser className="h-4 w-4 text-muted" aria-hidden="true" />
                {t('layout.profile')}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
            </>
          ) : null}
          <DropdownMenu.Label className="px-2 py-1 text-xs text-muted">
            {t('layout.user.language')}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={i18n.language} onValueChange={onLanguageChange}>
            {languages.map((lang) => (
              <DropdownMenu.RadioItem
                key={lang.culture}
                value={lang.culture}
                disabled={languageBusy}
                // Menü, `changeCulture` çözülene dek AÇIK kalır (kapanışı `onLanguageChange` yapar).
                onSelect={(event) => event.preventDefault()}
                aria-label={lang.name}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded py-1.5 pl-8 pr-2 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-60"
              >
                <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex">
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                </DropdownMenu.ItemIndicator>
                {lang.name}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={onLogout}
            className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-danger outline-none data-[highlighted]:bg-danger-subtle"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('layout.logout')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
